import {
  canonicalHqListNavigation,
  hqMateDirectory,
  hqListedMateIdentities,
  hqListedMates,
  organizationMateListProjects,
  EMPTY_MATE_DIRECTORY,
} from "./projections/hqMateDirectory.ts";
import { zeropsStructure } from "../zerops/hq/structure.ts";
import { mateHealth } from "./projections/mateHealth.ts";
/**
 * What a screen, and the account runtime's derivations outside React, may reach of the account's
 * data: the store's reads and the hold on a detail — never its writer. An app sets
 * {@link accountReadsAtom} in its registry while an account is mounted; atoms read through it.
 *
 * @module data/reads
 */
import { hqMateIdentities, type HqMateIdentity } from "./projections/hqMateIdentity.ts";
import { Atom, type AtomRegistry } from "effect/reactivity";

import type { ZeropsOrganization } from "../zerops/api.ts";
import type { DetailDemand } from "./demand.ts";
import { linkKeys } from "./model.ts";
import { mateVariablesScope } from "./families/mateVariables.ts";
import { hqMateSetup, UNKNOWN_MATE_SETUP, type HqMateSetup } from "./projections/hqMateSetup.ts";
import { mateVariables } from "./projections/mateVariables.ts";
import { projectProcesses, type ProjectProcesses } from "./projections/processes.ts";
import type { ProjectValue } from "./families/project.ts";
import { projectServices, projectsServices, type ProjectServices } from "./projections/services.ts";
import { projectUsage, type ProjectUsage } from "./projections/usage.ts";
import { publicAccess, type PublicAccess } from "./projections/publicAccess.ts";
import { PROJECT_ROUTINGS_LISTING } from "./families/publicRouting.ts";
import { creditForecast } from "./projections/creditForecast.ts";
import { balanceScope } from "./families/organizationBalance.ts";
import { creditScope } from "./families/organizationCredit.ts";
import { vault } from "./projections/vault.ts";
import type { VaultView } from "./projections/vaultModel.ts";
import {
  listedProject,
  organizationProjects,
  projectGone,
  projectStanding,
  type OrganizationProjects,
  type ProjectStanding,
} from "./projections/projects.ts";
import { mateAttention, matesAttention, attentionProjects } from "./projections/mateAttention.ts";
import {
  hqMateOverview,
  hqMateRefusal,
  hqMateAnswered,
  hqMatePresence,
  hqMateLogins,
  hqMateReady,
  hqMates,
  hqMatesAnswered,
  type HqMatesRead,
} from "./projections/hqMates.ts";
import type { MateLinkValue } from "./families/mateLink.ts";
import { mateLinks, mateOfEnvironment, type MateLinksRead } from "./projections/mateLinks.ts";
import {
  hqAppChanges,
  hqNavigation,
  canonicalHqMenuNavigation,
  type CanonicalHqNavigationRead,
  hqPersonFacts,
  hqStatus,
  type HqNavigationRead,
} from "./projections/hqNavigation.ts";
import type { HqAppValue, HqPersonFacts, HqStatusValue } from "./families/hqNavigation.ts";
import type { AccountStore } from "./store.ts";

export interface AccountReads {
  readonly viewer?: ZeropsOrganization | undefined;
  readonly data: AccountStore["data"];
  /** The organization whose navigation is observed; `null` before one is chosen. */
  readonly orgId: string | null;
  /** A hold on a detail while it is wanted; the release lets it go. */
  readonly demandDetail: (demand: DetailDemand) => () => void;
  /** Renews each held own row no push keeps current (a project's, naming everybody's grants). */
  readonly renewHeld: () => void;
}

/** The mounted account's reads; `null` while no account is mounted. */
export const accountReadsAtom = Atom.make<AccountReads | null>(null).pipe(
  Atom.keepAlive,
  Atom.withLabel("data:account-reads"),
);

export const NOT_READ_PROCESSES: ProjectProcesses = {
  retained: undefined,
  processes: undefined,
  running: [],
  live: false,
  reconnecting: false,
  history: "unread",
};

/** One project's processes as the mounted account holds them; not read without one. */
export const projectProcessesAtom = Atom.family((projectId: string) =>
  Atom.make((get): ProjectProcesses => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null) return NOT_READ_PROCESSES;
    return get(account.data.project(projectProcesses, { orgId: account.orgId, projectId }));
  }).pipe(Atom.withLabel(`data:project-processes:${projectId}`)),
);

export const NOT_READ_SERVICES: ProjectServices = {
  services: undefined,
  live: false,
  reconnecting: false,
};

/** One project's services as the mounted account holds them; not read without one. */
export const projectServicesAtom = Atom.family((projectId: string) =>
  Atom.make((get): ProjectServices => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null) return NOT_READ_SERVICES;
    return get(account.data.project(projectServices, { orgId: account.orgId, projectId }));
  }).pipe(Atom.withLabel(`data:project-services:${projectId}`)),
);

const NOT_READ_PUBLIC_ACCESS: PublicAccess = {
  state: "reading",
  routes: [],
  pending: [],
  offers: [],
  readsProject: false,
};

/** One project's public access, with its fallback listing held while a consumer reads it. */
export const projectPublicAccessAtom = Atom.family((projectId: string) => {
  const source = Atom.make((get): PublicAccess => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null) return NOT_READ_PUBLIC_ACCESS;
    return get(account.data.project(publicAccess, { orgId: account.orgId, projectId }));
  });
  const readsProject = Atom.make((get) => get(source).readsProject).pipe(
    Atom.withEquality((left, right) => left === right),
  );
  const demand = Atom.make((get) => {
    const account = get(accountReadsAtom);
    if (account !== null && get(readsProject))
      get.addFinalizer(
        account.demandDetail({
          family: "publicRouting",
          listing: PROJECT_ROUTINGS_LISTING,
          ownerId: projectId,
        }),
      );
  });
  return Atom.make((get): PublicAccess => {
    get(demand);
    return get(source);
  });
});

/**
 * The organization's credit read as far as it went: its generation, which a create dialog holds
 * at its opening (`creditForecast`'s `openedAt`); `0` without the mounted account's organization.
 */
export const creditReadGenerationAtom = Atom.family((orgId: string) =>
  Atom.make((get): number => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId !== orgId) return 0;
    return get(account.data.stream(creditScope(orgId))).generation;
  }).pipe(Atom.withLabel(`data:credit-read-generation:${orgId}`)),
);

/**
 * The organization's balance read as far as it went: its generation, which a create dialog holds
 * at its opening (`creditForecast`'s `balanceOpenedAt`); `0` without the mounted account's
 * organization.
 */
export const balanceReadGenerationAtom = Atom.family((orgId: string) =>
  Atom.make((get): number => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId !== orgId) return 0;
    return get(account.data.stream(balanceScope(orgId))).generation;
  }).pipe(Atom.withLabel(`data:balance-read-generation:${orgId}`)),
);

const creditForecastFamily = Atom.family((key: string) =>
  Atom.make((get): boolean => {
    const [credit = "", balance = "", ...org] = key.split(":");
    const openedAt = Number(credit);
    const balanceOpenedAt = Number(balance);
    const orgId = org.join(":");
    const account = get(accountReadsAtom);
    if (account === null || account.orgId !== orgId) return false;
    return get(account.data.project(creditForecast, { orgId, openedAt, balanceOpenedAt }));
  }).pipe(Atom.withLabel(`data:credit-forecast:${key}`)),
);

/**
 * Whether a new project in the organization the mounted account observes waits for credit, as
 * read since a dialog opened at `openedAt` (its credit read) and `balanceOpenedAt` (its balance
 * read) (`creditForecast`); `false` without one, or for another organization.
 */
export const creditForecastAtom = (orgId: string, openedAt: number, balanceOpenedAt: number) =>
  creditForecastFamily(`${String(openedAt)}:${String(balanceOpenedAt)}:${orgId}`);

export const NOT_READ_USAGE: ProjectUsage = {
  read: false,
  byService: {},
  history: [],
  failure: undefined,
};

/**
 * One project's resources (`usageOwnerOf`) as the mounted account holds them; not read without
 * one. Read only while a screen demands the owner's `usage` and `usageHistory` details.
 */
export const projectUsageAtom = Atom.family((owner: string) =>
  Atom.make((get): ProjectUsage => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null) return NOT_READ_USAGE;
    return get(account.data.project(projectUsage, { orgId: account.orgId, owner }));
  }).pipe(Atom.withLabel(`data:project-usage:${owner}`)),
);

export const NOT_READ_VAULT: VaultView = {
  status: "unread",
  complete: false,
  scopes: [],
  notLive: [],
};

/**
 * One project's vault, in the shown organization, as the mounted account holds it; not read
 * without one. Read only while a screen demands the project's `projectVariables` and
 * `serviceVariable` details and its process `history` (when services started, when it deployed).
 */
export const vaultAtom = Atom.family((projectId: string) =>
  Atom.make((get): VaultView => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null) return NOT_READ_VAULT;
    return get(account.data.project(vault, { orgId: account.orgId, projectId }));
  }).pipe(Atom.withLabel(`data:vault:${projectId}`)),
);

const NO_PROJECTS_SERVICES: Readonly<Record<string, ProjectServices>> = {};

/**
 * Several projects' services at once, as the mounted account holds them, by project id; nothing
 * without one. Keyed by the ids joined with `,`.
 */
export const projectsServicesAtom = Atom.family((projectIds: string) =>
  Atom.make((get): Readonly<Record<string, ProjectServices>> => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null || projectIds === "")
      return NO_PROJECTS_SERVICES;
    return get(
      account.data.project(projectsServices, {
        orgId: account.orgId,
        projectIds: projectIds.split(","),
      }),
    );
  }).pipe(Atom.withLabel(`data:projects-services:${projectIds}`)),
);

export const NOT_READ_PROJECTS: OrganizationProjects = {
  projects: [],
  read: "unread",
  complete: false,
  live: false,
  reconnecting: false,
};

/**
 * The organization's projects as the mounted account observes them — the organization it shows;
 * not read without one.
 */
export const shownProjectsAtom = Atom.make(
  (get): OrganizationProjects & { readonly orgId: string | null } => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null) return { ...NOT_READ_PROJECTS, orgId: null };
    return {
      ...get(account.data.project(organizationProjects, account.orgId)),
      orgId: account.orgId,
    };
  },
).pipe(Atom.withLabel("data:shown-projects"));

/** One project as the mounted account holds it; `null` without one, or while it holds none. */
export const listedProjectAtom = Atom.family((projectId: string) =>
  Atom.make((get): ProjectValue | null => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null) return null;
    return get(account.data.project(listedProject, { orgId: account.orgId, projectId }));
  }).pipe(Atom.withLabel(`data:listed-project:${projectId}`)),
);

/** Whether the mounted account's owner proved a project deleted; `false` without an account. */
export const projectGoneAtom = Atom.family((projectId: string) =>
  Atom.make((get): boolean => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null) return false;
    return get(account.data.project(projectGone, { orgId: account.orgId, projectId }));
  }).pipe(Atom.withLabel(`data:project-gone:${projectId}`)),
);

const NOT_KNOWN: ProjectStanding = { kind: "unknown" };

/** Where one project stands with the viewer as the mounted account holds it; not known without one. */
export const projectStandingAtom = Atom.family((projectId: string) =>
  Atom.make((get): ProjectStanding => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null) return NOT_KNOWN;
    return get(account.data.project(projectStanding, { orgId: account.orgId, projectId }));
  }).pipe(Atom.withLabel(`data:project-standing:${projectId}`)),
);

/**
 * Holds a project's newest process history from outside React, through whichever account is
 * mounted in `registry`: moved to a newly mounted one, let go on release.
 */
export function holdProjectHistory(
  registry: AtomRegistry.AtomRegistry,
  projectId: string,
): () => void {
  let release: (() => void) | null = null;
  const hold = (account: AccountReads | null) => {
    release?.();
    release =
      account?.demandDetail({ family: "process", listing: "history", ownerId: projectId }) ?? null;
  };
  const unsubscribe = registry.subscribe(accountReadsAtom, hold, { immediate: true });
  return () => {
    unsubscribe();
    release?.();
    release = null;
  };
}

export const NOT_READ_HQ: HqNavigationRead = {
  updateRequired: false,
  coreBuild: undefined,
  structure: null,
  organization: null,
  presses: {},
  people: {},
  read: "unread",
  refusal: null,
  capped: false,
  slow: false,
  live: false,
  reconnecting: false,
};

/** HQ's navigation of the organization the mounted account shows; not read without one. */
export const shownHqNavigationAtom = Atom.make(
  (get): HqNavigationRead & { readonly orgId: string | null } => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null) return { ...NOT_READ_HQ, orgId: null };
    return { ...get(account.data.project(hqNavigation, account.orgId)), orgId: account.orgId };
  },
).pipe(Atom.withLabel("data:shown-hq-navigation"));

const NO_MATES: HqMatesRead = { mates: {}, live: false };

/** The Mates HQ relays for the organization the mounted account shows; none without one. */
export const shownHqMatesAtom = Atom.make((get): HqMatesRead => {
  const account = get(accountReadsAtom);
  if (account === null || account.orgId === null) return NO_MATES;
  return get(account.data.project(hqMates, account.orgId));
}).pipe(Atom.withLabel("data:shown-hq-mates"));

/** Whether HQ answered for every Mate the organization shown places (`hqMatesAnswered`). */
export const shownHqMatesAnsweredAtom = Atom.make((get): boolean => {
  const account = get(accountReadsAtom);
  if (account === null || account.orgId === null) return false;
  return get(account.data.project(hqMatesAnswered, account.orgId));
}).pipe(Atom.withLabel("data:shown-hq-mates-answered"));

const NO_PERSON_FACTS: Readonly<Record<string, HqPersonFacts>> = {};

/** What HQ computed of the reader for each project it places, in the organization shown. */
export const shownHqPersonFactsAtom = Atom.make((get): Readonly<Record<string, HqPersonFacts>> => {
  const account = get(accountReadsAtom);
  if (account === null || account.orgId === null) return NO_PERSON_FACTS;
  return get(account.data.project(hqPersonFacts, account.orgId));
}).pipe(Atom.withLabel("data:shown-hq-person-facts"));

/** How the organization shown's HQ stands, as its navigation says it; `null` before it said. */
export const shownHqStatusAtom = Atom.make((get): HqStatusValue | null => {
  const account = get(accountReadsAtom);
  if (account === null || account.orgId === null) return null;
  return get(account.data.project(hqStatus, account.orgId));
}).pipe(Atom.withLabel("data:shown-hq-status"));
export const NO_MATE_LINKS: MateLinksRead = {
  targets: new Map(),
  machines: new Map(),
  containers: new Map(),
};

/** The Mates shown, as this tab read them from each Mate; none without an account. */
export const shownMateLinksAtom = Atom.make((get): MateLinksRead => {
  const account = get(accountReadsAtom);
  if (account === null) return NO_MATE_LINKS;
  return get(account.data.project(mateLinks, null));
}).pipe(Atom.withLabel("data:shown-mate-links"));

/** The Mate an environment is served by, as this tab read it; null while none read names it. */
export const mateOfEnvironmentAtom = Atom.family((environmentId: string) =>
  Atom.make((get): MateLinkValue | null => {
    const account = get(accountReadsAtom);
    if (account === null) return null;
    return get(account.data.project(mateOfEnvironment, environmentId));
  }).pipe(Atom.withLabel(`data:mate-of-environment:${environmentId}`)),
);

/**
 * Holds one service's own read from outside React, through whichever account is mounted in
 * `registry`: moved to a newly mounted one, let go on release.
 */
export function holdServiceRead(
  registry: AtomRegistry.AtomRegistry,
  serviceId: string,
): () => void {
  let release: (() => void) | null = null;
  const hold = (account: AccountReads | null) => {
    release?.();
    release =
      account?.demandDetail({ family: "service", listing: "service", ownerId: serviceId }) ?? null;
  };
  const unsubscribe = registry.subscribe(accountReadsAtom, hold, { immediate: true });
  return () => {
    unsubscribe();
    release?.();
    release = null;
  };
}

const NO_CHANGES: Readonly<Record<string, HqAppValue["changes"]>> = {};

/** Each application's open changes in the organization shown, as HQ's navigation says them. */
export const shownHqAppChangesAtom = Atom.make(
  (get): Readonly<Record<string, HqAppValue["changes"]>> => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null) return NO_CHANGES;
    return get(account.data.project(hqAppChanges, account.orgId));
  },
).pipe(Atom.withLabel("data:shown-hq-app-changes"));
/**
 * Holds one Mate's container's variables from outside React, through whichever account is mounted
 * in `registry`: read now, again each sampled interval while held, let go on release.
 */
export function holdMateVariables(
  registry: AtomRegistry.AtomRegistry,
  serviceId: string,
): () => void {
  let release: (() => void) | null = null;
  const hold = (account: AccountReads | null) => {
    release?.();
    release =
      account === null || account.orgId === null
        ? null
        : account.demandDetail({ family: "mateVariables", ownerId: serviceId });
  };
  const unsubscribe = registry.subscribe(accountReadsAtom, hold, { immediate: true });
  return () => {
    unsubscribe();
    release?.();
    release = null;
  };
}

/** Where a read of one of a Mate's container's variables stands for one that asks now. */
const variableNow = Atom.family((serviceId: string) =>
  Atom.make((get): boolean | "unknown" | "waiting" => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null) return "unknown";
    const stream = get(account.data.stream(mateVariablesScope(account.orgId, serviceId)));
    if (stream.phase === "live") {
      const read = get(account.data.project(mateVariables, { orgId: account.orgId, serviceId }));
      const value = read.flag;
      return typeof value === "boolean" ? value : "unknown";
    }
    // Waits only for a read the live link will make; a link down or refused is no answer.
    const link = get(account.data.stream(linkKeys.zerops(account.orgId)));
    if (link.phase !== "live") return "unknown";
    return stream.phase === "connecting" ||
      stream.phase === "baselining" ||
      stream.phase === "stale" ||
      stream.phase === "idle"
      ? "waiting"
      : "unknown";
  }),
);

/**
 * One of a Mate's container's variables as its read says it now: one read for the asking, or the
 * last within its freshness. A read that did not succeed or could not say is `"unknown"`, never
 * `false`. Each step of the read has its own deadline in the stream machine.
 */
export function readMateFlag(
  registry: AtomRegistry.AtomRegistry,
  serviceId: string,
): Promise<boolean | "unknown"> {
  const release = holdMateVariables(registry, serviceId);
  return new Promise((resolve) => {
    let unsubscribe: (() => void) | null = null;
    let settled = false;
    const answer = (now: boolean | "unknown" | "waiting") => {
      if (settled || now === "waiting") return;
      settled = true;
      unsubscribe?.();
      release();
      resolve(now);
    };
    unsubscribe = registry.subscribe(variableNow(serviceId), answer, {
      immediate: true,
    });
    if (settled) unsubscribe();
  });
}

/** A Mate’s setup evidence, already delivered by the organization’s navigation. No detail demand. */
export const hqMateSetupAtom = Atom.family((projectId: string) =>
  Atom.make((get): HqMateSetup => {
    const account = get(accountReadsAtom);
    if (account === null || account.orgId === null) return UNKNOWN_MATE_SETUP;
    return get(account.data.project(hqMateSetup, { orgId: account.orgId, projectId }));
  }),
);

/** Account/org switching replaces dependencies; every surface shares the same project reader. */
function projectReader<Key, Value>(
  projection: import("./store.ts").Projection<Key, Value>,
  key: (orgId: string, projectId: string) => Key,
  empty: Value,
) {
  return Atom.family((projectId: string) =>
    Atom.make((get): Value => {
      const account = get(accountReadsAtom);
      return account?.orgId == null
        ? empty
        : get(account.data.project(projection, key(account.orgId, projectId)));
    }),
  );
}
const mateKey = (orgId: string, projectId: string) => ({ orgId, projectId });
export const hqMateOverviewAtom = projectReader(hqMateOverview, mateKey, null);
export const hqMateRefusalAtom = projectReader(hqMateRefusal, mateKey, null);
export const hqMateAnsweredAtom = projectReader(hqMateAnswered, mateKey, false);
export const hqMatePresenceAtom = projectReader(hqMatePresence, mateKey, {
  presence: null,
  live: false,
});
export const hqMateLoginsAtom = projectReader(hqMateLogins, mateKey, undefined);
export const hqMateReadyAtom = projectReader(hqMateReady, mateKey, undefined);
export const mateHealthAtom = projectReader(mateHealth, mateKey, {
  health: null,
  live: false,
});
export const mateAttentionAtom = projectReader(mateAttention, mateKey, {
  attention: null,
  live: false,
  unseen: null,
});
export const shownAttentionProjectsAtom = Atom.make((get): ReadonlyArray<string> => {
  const account = get(accountReadsAtom);
  return account?.orgId == null ? [] : get(account.data.project(attentionProjects, account.orgId));
}).pipe(
  Atom.withEquality(
    (a: ReadonlyArray<string>, b) => a.length === b.length && a.every((id, i) => id === b[i]),
  ),
);
export const shownMatesAttentionAtom = Atom.make((get) => {
  const account = get(accountReadsAtom);
  return account?.orgId == null ? {} : get(account.data.project(matesAttention, account.orgId));
});

export const shownCanonicalHqMenuNavigationAtom = Atom.make(
  (get): CanonicalHqNavigationRead & { readonly orgId: string | null } => {
    const account = get(accountReadsAtom);
    return account?.orgId == null
      ? { ...NOT_READ_HQ, structure: null, orgId: null }
      : {
          ...get(account.data.project(canonicalHqMenuNavigation, account.orgId)),
          orgId: account.orgId,
        };
  },
);

/** Compatibility readers share the canonical menu derivation. */
export const shownHqMenuNavigationAtom = Atom.make((get) => {
  const navigation = get(shownCanonicalHqMenuNavigationAtom);
  return {
    ...navigation,
    structure: navigation.structure === null ? null : zeropsStructure(navigation.structure),
  };
});

/** The mounted account's HQ identities; names and faces share no platform listing dependency. */
export const shownHqMateIdentitiesAtom = Atom.make(
  (get): Readonly<Record<string, HqMateIdentity>> => {
    const account = get(accountReadsAtom);
    return account?.orgId == null ? {} : get(account.data.project(hqMateIdentities, account.orgId));
  },
).pipe(Atom.withLabel("data:shown-hq-mate-identities"));

/** Personal Chief and ordinary Mate lists share the account's single partition. */
export const shownHqMateDirectoryAtom = Atom.make((get) => {
  const account = get(accountReadsAtom);
  return account?.orgId == null
    ? EMPTY_MATE_DIRECTORY
    : get(account.data.project(hqMateDirectory, account.orgId));
});
export const shownMateListProjectsAtom = Atom.make((get) => {
  const account = get(accountReadsAtom);
  return account?.orgId == null
    ? { ...NOT_READ_PROJECTS, orgId: null }
    : {
        ...get(account.data.project(organizationMateListProjects, account.orgId)),
        orgId: account.orgId,
      };
});
export const shownCanonicalHqListNavigationAtom = Atom.make((get) => {
  const account = get(accountReadsAtom);
  return account?.orgId == null
    ? { ...NOT_READ_HQ, structure: null, orgId: null }
    : {
        ...get(account.data.project(canonicalHqListNavigation, account.orgId)),
        orgId: account.orgId,
      };
});
export const shownHqListNavigationAtom = Atom.make((get) => {
  const navigation = get(shownCanonicalHqListNavigationAtom);
  return {
    ...navigation,
    structure: navigation.structure === null ? null : zeropsStructure(navigation.structure),
  };
});
export const shownHqListedMateIdentitiesAtom = Atom.make(
  (get): Readonly<Record<string, HqMateIdentity>> => {
    const account = get(accountReadsAtom);
    return account?.orgId == null
      ? {}
      : get(account.data.project(hqListedMateIdentities, account.orgId));
  },
);
export const shownHqListedMatesAtom = Atom.make((get): HqMatesRead => {
  const account = get(accountReadsAtom);
  return account?.orgId == null
    ? NO_MATES
    : get(account.data.project(hqListedMates, account.orgId));
});
